/**
 * BYOB Easy Bundle SDK controller.
 * Single source of truth for the box. Components talk to it with document events:
 *   in:  byob:add {variantId}, byob:remove {productId}, byob:replace {lines, source},
 *        byob:submit {source, addons: {selected, all}} (addons = bottle variant ids, added to the cart next to the box),
 *        byob:request-state
 *        byob:request-catalog
 *   out: byob:catalog {products}, byob:update {counts, items, total, isFull, ready}, byob:submitting {source, state},
 *        byob:error {source, message}, byob:submitted {source}
 */
(() => {
  const bootEl = document.querySelector('[data-byob-sdk-boot]');
  if (!bootEl) return;

  const offerId = bootEl.dataset.offerId;
  const afterAdd = bootEl.dataset.afterAdd;
  const maxPerProduct = Number(bootEl.dataset.maxPerProduct) || Infinity;
  const singleUnitIds = (bootEl.dataset.singleUnitIds ?? '').split(',').filter(Boolean);
  const limitOf = (productId) => (singleUnitIds.includes(productId) ? 1 : maxPerProduct);
  // Box sizes and prices come from the offer's discount rules once the SDK is ready. Until then they come from
  // the sticky bar's theme settings; with neither, no size limit is enforced.
  const barEl = document.querySelector('byob-box-bar');
  const tiers = {
    low: Number(barEl?.dataset.countLow) || 1,
    high: Number(barEl?.dataset.countHigh) || Infinity,
    priceLow: null,
    priceHigh: null,
  };
  const INIT_WAIT = 25000;
  const INIT_RETRY_DELAY = 500;
  const CATALOG_WAIT = 8000;
  const LOG = '[byob-sdk]';

  let instance = null;
  let isSubmitting = false;
  let isReplacing = false;
  const catalog = new Map();

  const normaliseId = (value) => String(value ?? '').split('/').pop();
  const imageUrl = (image) => (typeof image === 'string' ? image : (image?.src ?? image?.url ?? image?.originalSrc ?? ''));
  const emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const emitError = (source, message) => emit('byob:error', { source, message: message || window.cartStrings?.error || '' });

  // The SDK can finish loading before this script listens for its event, so poll for it as well.
  const whenSdkReady = () =>
    new Promise((resolve) => {
      const deadline = Date.now() + INIT_WAIT;
      const check = () => {
        if (window.gbbSdk) return resolve(window.gbbSdk);
        if (Date.now() >= deadline) return resolve(null);
        window.setTimeout(check, 100);
      };
      document.addEventListener('gbb-sdk:loaded', check, { once: true });
      check();
    });

  const buildCatalog = () => {
    catalog.clear();
    for (const step of instance.f.getAllStepsData()) {
      for (const category of Object.values(step.categories || {})) {
        for (const product of category.products || []) {
          const thumbUrl = imageUrl(product.featured_image || product.images?.[0]);
          for (const variant of product.variants || []) {
            catalog.set(normaliseId(variant.variantId ?? variant.id), {
              stepId: step.stepId,
              categoryId: category.categoryId,
              productId: normaliseId(product.productId ?? product.id),
              title: product.title,
              thumbUrl: thumbUrl || imageUrl(variant.image),
            });
          }
        }
      }
    }
  };

  // The grid cards come from the offer's first category, so Unlisted products can be shown too.
  const emitCatalog = () => {
    const [firstStep] = instance.f.getAllStepsData();
    const [firstCategory] = Object.values(firstStep?.categories || {});
    const products = (firstCategory?.products || []).map((product) => {
      const variants = product.variants || [];
      const variant = variants.find((item) => item.available) ?? variants[0];
      return {
        productId: normaliseId(product.productId ?? product.id),
        variantId: normaliseId(variant?.variantId ?? variant?.id),
        handle: product.handle,
        title: product.title,
        type: product.productType || product.type || '',
        imageUrl: imageUrl(product.featured_image || product.images?.[0]),
        price: Number(variant?.price),
        available: Boolean(variant?.available),
      };
    });
    if (products.length) emit('byob:catalog', { products });
    return products.length > 0;
  };

  // The offer's product data can still be loading right after init, so keep asking until it is there.
  const emitCatalogWhenReady = async () => {
    const deadline = Date.now() + INIT_WAIT;
    while (!emitCatalog() && Date.now() < deadline) {
      await new Promise((resolve) => window.setTimeout(resolve, INIT_RETRY_DELAY));
    }
    // The variant lookup built at init can be empty if the products had not loaded yet.
    buildCatalog();
  };

  const readTiers = () => {
    const config = instance.bundleData?.discountConfiguration;
    const rules = (config?.rules || [])
      .map((rule) => ({ count: Number(rule.value), rule }))
      .filter(({ count }) => Number.isFinite(count) && count > 0)
      .sort((a, b) => a.count - b.count);

    if (!config?.isDiscountEnabled || !rules.length) {
      console.warn(LOG, 'no discount rules found on the offer, using the sticky bar settings', tiers);
      return;
    }

    // Only a fixed bundle price is a plain amount; other discount modes keep the theme's price labels.
    const isFixedPrice = config.discountMode === 'FIXED_BUNDLE_PRICE';
    const currency = window.Shopify?.currency?.active;
    const priceOf = ({ rule }) => {
      if (!isFixedPrice) return null;
      const price = Number(rule.discountValue_multipleCurrency?.[currency] ?? rule.discountValue);
      return Number.isFinite(price) ? price : null;
    };

    const low = rules[0];
    const high = rules[rules.length - 1];
    Object.assign(tiers, { low: low.count, high: high.count, priceLow: priceOf(low), priceHigh: priceOf(high) });
    console.info(LOG, 'box sizes from the offer', tiers);
  };

  const readItems = () => {
    const items = new Map();
    for (const line of instance.f.getCartItems() || []) {
      const props = line.properties || {};
      if (props._defaultProduct || props._DefaultProduct) continue;

      const meta = catalog.get(normaliseId(line.variant_id ?? line.variantId ?? line.id));
      const productId = normaliseId(line.product_id ?? meta?.productId);
      const entry = items.get(productId) ?? {
        productId,
        variantId: normaliseId(line.variant_id ?? line.variantId ?? line.id),
        title: meta?.title ?? line.product_title ?? line.title ?? '',
        thumbUrl: meta?.thumbUrl || imageUrl(line.featured_image || line.image),
        key: props._uniqueGbbSdkItemKey,
        qty: 0,
      };
      entry.qty += line.quantity || 0;
      items.set(productId, entry);
    }
    return items;
  };

  const getTotal = (items) => {
    let total = 0;
    for (const item of items.values()) total += item.qty;
    return total;
  };

  const broadcast = () => {
    if (!instance || isReplacing) return;
    const items = readItems();
    const counts = {};
    for (const [productId, item] of items) counts[productId] = item.qty;
    const total = getTotal(items);
    emit('byob:update', {
      counts,
      items: [...items.values()],
      total,
      isFull: total >= tiers.high,
      ready: true,
      tiers: { ...tiers },
      limits: { max: maxPerProduct, singleUnitIds },
    });
  };

  // A click can land before the offer's product data has loaded: rebuild the lookup and wait a little for it.
  const findMeta = async (variantId) => {
    const deadline = Date.now() + CATALOG_WAIT;
    let meta = catalog.get(normaliseId(variantId));
    while (!meta && Date.now() < deadline) {
      await new Promise((resolve) => window.setTimeout(resolve, INIT_RETRY_DELAY));
      buildCatalog();
      meta = catalog.get(normaliseId(variantId));
    }
    return meta;
  };

  const add = async (source, variantId) => {
    const meta = await findMeta(variantId);
    if (!meta) {
      console.warn(LOG, 'variant is not in the offer', variantId);
      emitError(source, '');
      return false;
    }

    const items = readItems();
    if (getTotal(items) >= tiers.high) return false;

    const existing = items.get(meta.productId);
    if (existing && existing.qty >= limitOf(meta.productId)) return false;

    const result = existing
      ? await instance.f.updateCartItem(existing.key, { updateQuantityBy: 1, showToast: false })
      : await instance.f.addProductToBundle({
          stepId: meta.stepId,
          categoryId: meta.categoryId,
          variantId,
          quantity: 1,
          showToast: false,
        });

    if (!result?.ok) {
      emitError(source, result?.data?.message);
      return false;
    }
    return true;
  };

  const remove = async (source, productId) => {
    const existing = readItems().get(normaliseId(productId));
    if (!existing) return;

    const result = await instance.f.updateCartItem(existing.key, { updateQuantityBy: -1, showToast: false });
    if (!result?.ok) emitError(source, result?.data?.message);
  };

  const openStoreCart = async () => {
    const icon = document.getElementById('cart-icon-bubble') || document.querySelector('.header__icon--cart');
    if (!icon) return;

    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
      const sections = await response.json();
      const iconSection = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'] ?? '', 'text/html')
        .querySelector('.shopify-section');
      if (iconSection) icon.innerHTML = iconSection.innerHTML;
    } catch (error) {
      console.error(LOG, error);
    }

    const blockCartPage = (event) => {
      if (!event.defaultPrevented) event.preventDefault();
    };
    window.addEventListener('click', blockCartPage, { once: true });
    icon.click();
    window.removeEventListener('click', blockCartPage);
  };

  const DRAWER_STYLE_ID = 'byob-hide-store-drawer';
  const CART_ICON_SELECTOR = '#cart-icon-bubble, .header__icon--cart';

  // Shiprocket's Smart Cart opens its drawer on every add to cart. Hide it while the box goes to checkout.
  const hideStoreDrawer = () => {
    if (document.getElementById(DRAWER_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = DRAWER_STYLE_ID;
    style.textContent = '#shiprocket-smart-cart-root { display: none !important; } html, body { overflow: auto !important; position: static !important; height: auto !important; }';
    document.head.appendChild(style);
    document.querySelector(CART_ICON_SELECTOR)?.addEventListener('click', showStoreDrawer, { once: true });
  };

  const showStoreDrawer = () => {
    document.getElementById(DRAWER_STYLE_ID)?.remove();
    document.body.classList.remove('srsc-overflow-hidden');
  };

  // Coming back from checkout with the Back button restores this page from the browser's back/forward
  // cache, including the scroll lock Shiprocket's checkout set on the page. Undo it on restore.
  const unlockScroll = () => {
    [document.documentElement, document.body].forEach((element) => {
      element.style.removeProperty('overflow');
      element.style.removeProperty('position');
      element.style.removeProperty('height');
    });
    document.body.classList.remove('srsc-overflow-hidden');
  };

  const CHECKOUT_FLAG = 'byob-sent-to-checkout';

  window.addEventListener('pageshow', (event) => {
    let wentToCheckout = false;
    try {
      wentToCheckout = sessionStorage.getItem(CHECKOUT_FLAG) === '1';
      sessionStorage.removeItem(CHECKOUT_FLAG);
    } catch (error) {
      // storage blocked: fall back to the bfcache signal only
    }
    if (!event.persisted && !wentToCheckout) return;

    unlockScroll();
    hideStoreDrawer();
    isSubmitting = false;
    emit('byob:submitting', { source: 'bar', state: false });
    emit('byob:submitting', { source: 'picks', state: false });
    broadcast();
  });

  const CHECKOUT_LOADER_MAX = 8000;

  // Shiprocket's checkout opens as an overlay on this same page, so closing it never reloads the page.
  // Drop the loader as soon as the overlay is added (or after a safety timeout), not when it closes.
  const releaseWhenCheckoutAppears = (release) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
      window.clearTimeout(timer);
      release();
    };
    const observer = new MutationObserver((mutations) => {
      const added = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) => node.nodeType === 1 && !['SCRIPT', 'STYLE', 'LINK'].includes(node.tagName)
        )
      );
      if (added) window.setTimeout(finish, 400);
    });
    const timer = window.setTimeout(finish, CHECKOUT_LOADER_MAX);

    observer.observe(document.body, { childList: true });
    observer.observe(document.documentElement, { childList: true });
    // Shiprocket adds its checkout container to the body, not to the html element.
    observer.observe(document.body, { childList: true });
  };
  const SHIPROCKET_WAIT = 3000;
  const SHIPROCKET_POLL = 150;

  const openShiprocketCheckout = async () => {
    const selector = '[data-byob-shiprocket] .sr-headless-checkout, [data-byob-shiprocket] button';
    const deadline = Date.now() + SHIPROCKET_WAIT;

    while (Date.now() < deadline) {
      const button = document.querySelector(selector);
      if (button) {
        button.click();
        return true;
      }
      await new Promise((resolve) => window.setTimeout(resolve, SHIPROCKET_POLL));
    }

    console.warn(LOG, 'Shiprocket checkout button not found, using the store checkout');
    return false;
  };

  const goToCheckout = () => {
    window.location.assign(`${window.routes?.root_url ?? '/'}checkout`.replace('//', '/'));
  };

  // Bottles ticked in the popup are add-ons: plain cart lines next to the box, not part of it. Setting the exact
  // quantity (1 or 0) keeps this safe to repeat if the shopper comes back from checkout and submits again.
  const syncAddons = async (addons) => {
    if (!addons?.all?.length) return true;

    const updates = Object.fromEntries(addons.all.map((variantId) => [variantId, addons.selected.includes(variantId) ? 1 : 0]));
    const response = await fetch(window.routes?.cart_update_url ?? '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return response.ok;
  };

  const submit = async (source, addons) => {
    if (!instance || isSubmitting) return false;

    const total = getTotal(readItems());
    if (total !== tiers.low && total !== tiers.high) return false;

    const check = instance.f.validateBeforeAddBundleToCart({ showToast: false });
    if (!check?.ok) {
      emitError(source, check?.data?.message);
      return false;
    }

    isSubmitting = true;
    let keepLoading = false;
    emit('byob:submitting', { source, state: true });
    if (afterAdd !== 'drawer') hideStoreDrawer();

    try {
      if (source === 'bar' && !(await syncAddons(addons))) {
        showStoreDrawer();
        emitError(source, '');
        return false;
      }

      const result = await instance.f.addBundleToCart({ showToast: false });
      if (!result?.ok) {
        showStoreDrawer();
        emitError(source, result?.data?.message);
        return false;
      }

      emit('byob:submitted', { source });
      try {
        sessionStorage.setItem(CHECKOUT_FLAG, '1');
      } catch (error) {
        // storage blocked: the bfcache path still unlocks
      }

      if (afterAdd === 'shiprocket' && (await openShiprocketCheckout())) {
        keepLoading = true;
        return true;
      }

      if (afterAdd === 'shiprocket' || afterAdd === 'checkout') {
        keepLoading = true;
        goToCheckout();
        return true;
      }

      document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { source }, bubbles: true }));
      await openStoreCart();
      return true;
    } catch (error) {
      console.error(LOG, error);
      showStoreDrawer();
      emitError(source, '');
      return false;
    } finally {
      // Checkout takes a moment to appear: keep the loader until it does (or the safety timeout passes).
      const release = () => {
        isSubmitting = false;
        emit('byob:submitting', { source, state: false });
        broadcast();
      };
      if (keepLoading) releaseWhenCheckoutAppears(release);
      else release();
    }
  };

  const replace = async (source, lines = []) => {
    if (isReplacing || isSubmitting) return;
    isReplacing = true;

    try {
      await instance.f.clearCartItems();

      for (const line of lines) {
        for (let count = 0; count < line.qty; count += 1) {
          if (!(await add(source, line.variantId))) return;
        }
      }

      await submit(source);
    } finally {
      isReplacing = false;
      broadcast();
    }
  };

  const bindEvents = () => {
    document.addEventListener('byob:submit', (event) => submit(event.detail?.source ?? 'bar', event.detail?.addons));
    document.addEventListener('byob:replace', (event) => replace(event.detail.source, event.detail.lines));
    document.addEventListener('byob:request-state', broadcast);
    document.addEventListener('byob:request-catalog', emitCatalog);

    const { sdk } = window.byobSdk;
    sdk.subscribe(sdk.events.CART_ITEM_UPDATED, broadcast);
    sdk.subscribe(sdk.events.BUNDLE_STATE_RESET, broadcast);
  };

  // Clicks that arrive before the SDK has started (fallback cards) wait for it instead of being lost.
  let markReady;
  const whenReady = new Promise((resolve) => {
    markReady = resolve;
  });
  document.addEventListener('byob:add', async (event) => {
    if (!(await whenReady)) return emitError('grid', '');
    add('grid', event.detail.variantId);
  });
  document.addEventListener('byob:remove', async (event) => {
    if (!(await whenReady)) return emitError('grid', '');
    remove('grid', event.detail.productId);
  });

  const start = async () => {
    const sdk = await whenSdkReady();
    if (!sdk) {
      console.error(LOG, 'the Easy Bundle SDK did not load');
      markReady(false);
      return;
    }

    const initFetch = (steps) => Object.fromEntries(Object.keys(steps).map((stepId) => [stepId, 'all']));
    const deadline = Date.now() + INIT_WAIT;
    let lastError = null;

    while (!instance && Date.now() < deadline) {
      try {
        instance = await sdk.init({ offerId, initFetch });
      } catch (error) {
        lastError = error;
      }

      if (!instance) await new Promise((resolve) => window.setTimeout(resolve, INIT_RETRY_DELAY));
    }

    if (!instance && lastError) console.warn(LOG, 'last init error', lastError?.message);

    if (!instance) {
      console.error(LOG, 'init failed for', offerId);
      markReady(false);
      return;
    }

    window.byobSdk = { sdk, instance };
    readTiers();
    buildCatalog();
    bindEvents();
    markReady(true);
    emitCatalogWhenReady();
    broadcast();
  };

  start();
})();
